import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useTenant } from '../hooks/useTenant'
import { useProgram } from '../contexts/ProgramContext'
import { supabase } from '../lib/supabase'

interface LogicModelSummary {
  id: string;
  name: string;
  status: string;
  inputs_count: number;
  activities_count: number;
  outputs_count: number;
  outcomes_count: number;
  impact_goals_count: number;
}

// Stat card component
function StatCard({ 
  icon, 
  label, 
  value, 
  linkTo 
}: { 
  icon: string
  label: string
  value: number | string
  linkTo?: string 
}) {
  const content = (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center">
        <span className="text-3xl mr-4">{icon}</span>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  )

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>
  }
  return content
}

// Quick action button component
function QuickAction({ 
  icon, 
  label, 
  description, 
  linkTo 
}: { 
  icon: string
  label: string
  description: string
  linkTo: string 
}) {
  return (
    <Link 
      to={linkTo}
      className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow group"
    >
      <span className="text-2xl mr-4">{icon}</span>
      <div className="flex-1">
        <p className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
          {label}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const { organization, isLoading } = useTenant()
  const { programs, selectedProgram } = useProgram()
  const [logicModel, setLogicModel] = useState<LogicModelSummary | null>(null)
  const [loadingLM, setLoadingLM] = useState(false)
  
  // Count active programs (exclude archived)
  const activePrograms = programs.filter(p => p.status !== 'archived').length

  // Fetch logic model for selected program
  useEffect(() => {
    async function fetchLogicModel() {
      if (!selectedProgram?.id) {
        setLogicModel(null)
        return
      }

      setLoadingLM(true)
      try {
        // Get the logic model with counts
        const { data: lm } = await supabase
          .from('logic_models')
          .select('id, name, status')
          .eq('program_id', selectedProgram.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single()

        if (lm) {
          // Get counts for each element type
          const [inputs, activities, outputs, outcomes, goals] = await Promise.all([
            supabase.from('inputs').select('id', { count: 'exact', head: true }).eq('logic_model_id', lm.id),
            supabase.from('activities').select('id', { count: 'exact', head: true }).eq('logic_model_id', lm.id),
            supabase.from('logic_model_outputs').select('id', { count: 'exact', head: true }).eq('logic_model_id', lm.id),
            supabase.from('outcomes').select('id', { count: 'exact', head: true }).eq('logic_model_id', lm.id),
            supabase.from('impact_goals').select('id', { count: 'exact', head: true }).eq('logic_model_id', lm.id),
          ])

          setLogicModel({
            ...lm,
            inputs_count: inputs.count || 0,
            activities_count: activities.count || 0,
            outputs_count: outputs.count || 0,
            outcomes_count: outcomes.count || 0,
            impact_goals_count: goals.count || 0,
          })
        } else {
          setLogicModel(null)
        }
      } catch (err) {
        console.error('Error fetching logic model:', err)
        setLogicModel(null)
      } finally {
        setLoadingLM(false)
      }
    }

    fetchLogicModel()
  }, [selectedProgram?.id])

  return (
    <div className="max-w-6xl mx-auto">
      {/* Welcome header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {isLoading ? (
            'Loading organization...'
          ) : organization ? (
            <>Organization: <span className="font-medium">{organization.name}</span></>
          ) : (
            'No organization set up yet'
          )}
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard 
          icon="📋" 
          label={selectedProgram ? "Selected Program" : "Programs"} 
          value={selectedProgram ? selectedProgram.name : activePrograms} 
          linkTo="/programs" 
        />
        <StatCard 
          icon="📊" 
          label="Reports" 
          value={0} 
        />
        <StatCard 
          icon="👥" 
          label="Team Members" 
          value={1} 
        />
      </div>

      {/* Logic Model Summary */}
      {selectedProgram && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Logic Model
          </h2>
          
          {loadingLM ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="animate-pulse flex items-center">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
            </div>
          ) : logicModel ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">🎯</span>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{logicModel.name}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      logicModel.status === 'active' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {logicModel.status}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/programs/${selectedProgram.id}/logic-model/${logicModel.id}`}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  Edit →
                </Link>
              </div>
              
              {/* Logic Model Flow Mini */}
              <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="text-center flex-1">
                  <span className="text-lg">📦</span>
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-400">{logicModel.inputs_count}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Inputs</p>
                </div>
                <span className="text-gray-300 dark:text-gray-600">→</span>
                <div className="text-center flex-1">
                  <span className="text-lg">⚡</span>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">{logicModel.activities_count}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Activities</p>
                </div>
                <span className="text-gray-300 dark:text-gray-600">→</span>
                <div className="text-center flex-1">
                  <span className="text-lg">📊</span>
                  <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{logicModel.outputs_count}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Outputs</p>
                </div>
                <span className="text-gray-300 dark:text-gray-600">→</span>
                <div className="text-center flex-1">
                  <span className="text-lg">📈</span>
                  <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{logicModel.outcomes_count}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Outcomes</p>
                </div>
                <span className="text-gray-300 dark:text-gray-600">→</span>
                <div className="text-center flex-1">
                  <span className="text-lg">🎯</span>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{logicModel.impact_goals_count}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Impact</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">🎯</span>
                  <div>
                    <h3 className="font-medium text-blue-900 dark:text-blue-100">No Logic Model Yet</h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300">Create a logic model for {selectedProgram.name}</p>
                  </div>
                </div>
                <Link
                  to={`/programs/${selectedProgram.id}/logic-model/new`}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Create Logic Model
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick actions */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <QuickAction
            icon="➕"
            label="Create your first program"
            description="Define a new program with logic model"
            linkTo="/programs"
          />
          <QuickAction
            icon="👤"
            label="Invite team members"
            description="Add collaborators to your organization"
            linkTo="/settings"
          />
          <QuickAction
            icon="🏢"
            label="Organization settings"
            description="Configure your organization details"
            linkTo="/settings/organization"
          />
          <QuickAction
            icon="📈"
            label="View impact reports"
            description="Generate and view impact reports"
            linkTo="/programs"
          />
        </div>
      </div>

      {/* Getting started guide */}
      {!organization && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
            🚀 Getting Started
          </h3>
          <p className="text-blue-700 dark:text-blue-300 mb-4">
            Welcome to Impact Dashboard! Here's how to get started:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-blue-700 dark:text-blue-300">
            <li>Set up your organization in <Link to="/settings/organization" className="underline hover:no-underline">Organization Settings</Link></li>
            <li>Create your first program with a logic model</li>
            <li>Track outcomes and generate impact reports</li>
          </ol>
        </div>
      )}
    </div>
  )
}
